import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format } from 'date-fns';
import { getSecurityMetrics } from '../../../api/systemApi';
import { useWeek } from '../../../contexts/WeekContext';
import LoginCharts from './LoginCharts';
import FailedLoginDetailsTable from './FailedLoginDetailsTable';
import SuspiciousActivityTable from './SuspiciousActivityTable';
import SecurityConfigurationPanel from './SecurityConfigurationPanel';

// Helper functions for security event classification
const getSeverityLevel = (eventType) => {
    const severity = {
        'Critical': ['Attack Detected', 'Account Compromise', 'Brute Force Attack', 'Breached Password'],
        'High': ['Suspicious IP Address', 'Account Locked', 'Malicious Activity', 'Intrusion Attempt'],
        'Medium': ['Failed Password Attempt', 'Rate Limit Exceeded', 'Security Warning', 'Suspicious Activity'],
        'Low': ['MFA Event', 'Password Changed', 'User Deleted', 'Anomaly Detected']
    };
    
    for (const [level, types] of Object.entries(severity)) {
        if (types.includes(eventType)) return level;
    }
    return 'Low';
};

const getEventCategory = (eventType) => {
    const categories = {
        'authentication': ['Failed Password Attempt', 'Failed User Attempt (User Not Found)', 'MFA Event'],
        'security': ['Suspicious IP Address', 'Brute Force Attack', 'Account Locked', 'Rate Limit Exceeded'],
        'account': ['Password Changed', 'User Deleted', 'Account Compromise'],
        'threats': ['Attack Detected', 'Malicious Activity', 'Fraudulent Activity', 'Intrusion Attempt'],
        'warnings': ['Security Warning', 'Suspicious Activity', 'Anomaly Detected']
    };
    
    for (const [category, types] of Object.entries(categories)) {
        if (types.includes(eventType)) return category;
    }
    return 'other';
};

const SecurityTab = () => {
    const { dateRange } = useWeek();
    const [securityData, setSecurityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch security metrics
    const fetchSecurityMetrics = async () => {
        try {
            setLoading(true);
            setError(null);
            const from = format(dateRange.start, 'yyyy-MM-dd');
            const to = format(dateRange.end, 'yyyy-MM-dd');
            const data = await getSecurityMetrics(from, to);
            setSecurityData(data);
        } catch (err) {
            console.error('Failed to fetch security metrics:', err);
            setError('Failed to load security metrics');
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when date range changes
    useEffect(() => {
        if (dateRange.start && dateRange.end) {
            fetchSecurityMetrics();
        }
    }, [dateRange.start, dateRange.end]);

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            fetchSecurityMetrics();
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-row justify-between items-center">
                    <h3 className="subheader-textbox">
                        {/* <Icon icon='mdi:shield-check' className="text-xl text-primary" /> */}
                        Security Dashboard
                    </h3>
                </div>
                <div className="flex items-center justify-center p-8">
                    <div className="text-font-secondary">Loading security metrics...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 w-full overflow-y-auto overflow-x-hidden scrollable">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0">
                <h3 className="subheader-textbox">
                    {/* <Icon icon='mdi:shield-check' className="text-xl text-primary" /> */}
                    Security Dashboard
                </h3>
                <div className="text-sm text-font-secondary">
                    <span>
                        Showing data from <span className="font-bold text-primary">{format(new Date(), 'MMM d, yyyy')}</span>
                    </span>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-400 bg-opacity-20 border border-red-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-font-primary text-sm font-medium">Error</span>
                    </div>
                    <p className="text-font-primary text-xs">{error}</p>
                </div>
            )}

            <div className="flex flex-col gap-3">
                {/* Security Summary */}
                {securityData?.summary && (
                    <div className="flex flex-col gap-3">
                        {/* Main Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="card-container p-3 rounded-md">
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:login" className="text-3xl text-green-500" />
                                    <div className="flex flex-row items-center gap-2">
                                        <div className="text-2xl font-bold text-font-primary">
                                            {securityData.summary.totalSuccessfulLogins || 0}
                                        </div>
                                        <div className="text-base text-font-secondary font-semibold">Successful Logins</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-container p-3 rounded-md">
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:login" className="text-3xl text-red-500" />
                                    <div className="flex flex-row items-center gap-2">
                                        <div className="text-2xl font-bold text-font-primary">
                                            {securityData.summary.totalFailedLogins || 0}
                                        </div>
                                        <div className="text-base text-font-secondary font-semibold">Failed Logins</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-container p-3 rounded-md">
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:alert-circle" className="text-3xl text-yellow-500" />
                                    <div className="flex flex-row items-center gap-2">
                                        <div className="text-2xl font-bold text-font-primary">
                                            {securityData.suspiciousActivity?.length || 0}
                                        </div>
                                        <div className="text-base text-font-secondary font-semibold">Suspicious Activities</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Security Event Statistics */}
                        {securityData.suspiciousActivity && securityData.suspiciousActivity.length > 0 && (
                            <div className="flex flex-col gap-4">
                                {/* Severity Breakdown */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {(() => {
                                        const severityStats = {};
                                        securityData.suspiciousActivity.forEach(event => {
                                            const severity = getSeverityLevel(event.type);
                                            severityStats[severity] = (severityStats[severity] || 0) + 1;
                                        });
                                        
                                        const severityConfig = {
                                            'Critical': { color: 'text-red-600', icon: 'mdi:alert-octagon' },
                                            'High': { color: 'text-orange-500', icon: 'mdi:alert' },
                                            'Medium': { color: 'text-yellow-500', icon: 'mdi:alert-circle-outline' },
                                            'Low': { color: 'text-green-500', icon: 'mdi:information-outline' }
                                        };
                                        
                                        return Object.entries(severityStats).map(([severity, count]) => (
                                            <div key={severity} className="card-container p-3 rounded-md">
                                                <div className="flex items-center gap-2">
                                                    <Icon 
                                                        icon={severityConfig[severity]?.icon || 'mdi:alert'} 
                                                        className={`text-3xl ${severityConfig[severity]?.color || 'text-gray-500'}`} 
                                                    />
                                                    <div className="flex flex-row items-center gap-2">
                                                        <div className="text-2xl font-bold text-font-primary">
                                                            {count}
                                                        </div>
                                                        <div className="text-base text-font-secondary font-semibold">
                                                            {severity} Severity
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                                <hr className='border-border' />
                                <div className="flex items-center gap-1">
                                    <Icon icon="mdi:format-list-numbered" className="text-xl text-primary" />
                                    <h4 className="font-semibold text-font-primary">Top Security Events</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {(() => {
                                        const typeStats = {};
                                        const typeDescriptions = {};
                                        
                                        securityData.suspiciousActivity.forEach(event => {
                                            typeStats[event.type] = (typeStats[event.type] || 0) + 1;
                                            if (!typeDescriptions[event.type]) {
                                                typeDescriptions[event.type] = event.description;
                                            }
                                        });
                                        
                                        return Object.entries(typeStats)
                                            .sort(([,a], [,b]) => b - a)
                                            .slice(0, 4)
                                            .map(([type, count]) => (
                                                <div key={type} className="card-container p-3 rounded-md">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="text-xl font-bold text-primary">{count}</div>
                                                        <div className="text-sm font-bold text-font-primary">{type}</div>
                                                    </div>
                                                    <div className="text-sm text-font-primary">
                                                        {typeDescriptions[type] || 'No description available'}
                                                    </div>
                                                </div>
                                            ));
                                    })()}
                                </div>

                                {/* Recent Activity */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center gap-1">
                                        <Icon icon="mdi:clock-outline" className="text-xl text-primary" />
                                        <h4 className="font-semibold text-font-primary">Recent Security Activity</h4>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                        {securityData.suspiciousActivity
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .slice(0, 3)
                                            .map((event, index) => (
                                                <div key={index} className="card-container p-3 rounded-md">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="text-sm font-bold text-font-primary">
                                                            {event.type}
                                                        </div>
                                                        <div className="text-xs text-font-secondary">
                                                            {format(new Date(event.date), 'MMM d, yyyy HH:mm')}
                                                        </div>
                                                        <div className="text-sm text-font-primary mt-1">
                                                            {event.description || 'No description available'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <hr className='border-border' />

                {/* Charts and Tables */}
                <LoginCharts 
                    successfulLogins={securityData?.successfulLogins || []}
                    failedLogins={securityData?.failedLogins || []}
                />
                <hr className='border-border' />
                <FailedLoginDetailsTable 
                    failedLoginDetails={securityData?.failedLoginDetails || []}
                />
                <hr className='border-border' />
                <SuspiciousActivityTable 
                    suspiciousActivity={securityData?.suspiciousActivity || []}
                />
                <hr className='border-border' />
                {/* Security Configuration */}
                <SecurityConfigurationPanel />
            </div>
        </div>
    );
};

export default SecurityTab;
